import React, { useState, useEffect } from 'react';
import {Box, Container} from '@mui/material';
import {useNavigate, useParams} from 'react-router-dom';
import WbTextField from '../../components/common/WbTextField';
import WbButton from '../../components/common/WbButton';
import WbLoader from '../../components/common/WbLoader';
import KioskLayout from '../../components/kiosk/KioskLayout';
import {useKiosk} from '../../contexts/KioskContext';
import kioskApi from '../../services/api/kiosk/kioskApi';
import paths from '../../routes/paths';
import {getWelbeeHomeUrl, schoolHasUrn} from '../../utils/kioskAccess';
import './KioskStyles.scss';

export default function KioskUrnScreen() {
  const { schoolSlug } = useParams();
  const { school, loading: schoolLoading, markUrnVerified } = useKiosk();
  const navigate = useNavigate();
  const [urn, setUrn] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);

  // Defensive: a school with no URN can't run a kiosk. KioskApp already redirects these home, but
  // guard here too so this screen is never usable without a URN to verify against.
  const noUrn = !schoolLoading && !!school && !schoolHasUrn(school);
  useEffect(() => {
    if (noUrn) {
      window.location.href = getWelbeeHomeUrl();
    }
  }, [noUrn]);

  const handleVerify = async () => {
    // Guard against double-submit (e.g. repeated Enter presses before `verifying` disables the UI),
    // which would fire concurrent verify requests and race the navigation/error state.
    if (verifying) {
      return;
    }

    const trimmedUrn = urn.trim();
    if (!trimmedUrn) {
      setError('Please enter your school URN.');
      return;
    }

    setVerifying(true);
    setError(null);

    try {
      const result = await kioskApi.verifyURN(schoolSlug, trimmedUrn);
      if (result.isValid) {
        markUrnVerified();
        navigate(paths.kioskSchool.replace(':schoolSlug', schoolSlug));
      } else {
        setError(result.message || 'URN not matched. Please check the number and try again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'We couldn’t verify that URN. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  if (schoolLoading || noUrn) {
    return (
      <KioskLayout>
        <WbLoader />
      </KioskLayout>
    );
  }

  return (
    <KioskLayout>
      <Container maxWidth="md">
        <Box className="kiosk-card">
          <div className="kiosk-title">
            Verify your school
          </div>
          <div className="kiosk-subtitle">
            Enter the URN for {school?.schoolName} to continue.
          </div>

          <Box sx={{ mb: 5, mt: 2 }}>
            <WbTextField
              label="School URN"
              value={urn}
              onChange={(e) => {
                setUrn(e.target.value);
                setError(null);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleVerify();
                }
              }}
              className="kiosk-text-field-large"
              autoFocus
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            />
          </Box>

          {error && (
            <Box className="kiosk-error" sx={{ mb: 4 }}>
              {error}
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <WbButton
              CustomButtonText={verifying ? 'Verifying…' : 'Continue'}
              onClick={handleVerify}
              disabled={verifying || !urn.trim()}
              className="kiosk-button-large"
              sx={{
                backgroundColor: 'var(--kiosk-primary-color, #53B5E0)',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#3a9bc4',
                },
                '&:disabled': {
                  backgroundColor: '#9CA3AF',
                }
              }}
            />
            <Box
              component="button"
              type="button"
              onClick={() => { window.location.href = getWelbeeHomeUrl(); }}
              sx={{
                background: 'none',
                border: 'none',
                color: '#6B7280',
                cursor: 'pointer',
                fontSize: '14px',
                textDecoration: 'underline',
              }}
            >
              This isn’t my school — exit
            </Box>
          </Box>
        </Box>
      </Container>
    </KioskLayout>
  );
}
